import React, { useMemo, useState,useEffect} from "react";
import {
  Box,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  Alert,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  BarChart as BarChartIcon,
  TableChart as TableChartIcon,
  InfoOutlined as InfoOutlinedIcon,
} from "@mui/icons-material";
import { useProjectStore } from "../stores/project.store";
import ResultsPlots from "./ResultsPlots.component";
import ResultsTable from "./ResultsTable.component";
import projectsService from "../services/projects.service";
import { downloadFullArtifactCsv } from "../services/projects.service";
import { getStepRows } from "../utils/getStepRows";
import { selectViewedStepResult } from "../utils/viewedStepResult";
import { legacyPreviewRequest, mergeRefreshedPreview } from "../utils/previewRefresh";



export interface ResultsProps {
  onBack?: () => void;
  onContinue?: () => void;
  stepResults?: any; // Optional step-specific results to override global backendResult
  currentStep?: string;
  onResampleQc?: (args: { step: string; seed: number; artifactId: string; selectedGene?: string }) => void;


}



 type PlotSample = {
  n_genes_by_counts: number[];
  total_counts: number[];
  pct_counts_mt: number[];
};

type QcRow = {
  n_genes_by_counts: number;
  total_counts: number;
  pct_counts_mt: number;
};

const buildRowsFromPlotSample = (ps?: PlotSample): QcRow[] => {
  const n = ps?.n_genes_by_counts?.length ?? 0;
  if (!n) return [];
  return Array.from({ length: n }, (_: unknown, i: number) => ({
    n_genes_by_counts: ps!.n_genes_by_counts[i],
    total_counts: ps!.total_counts?.[i] ?? 0,
    pct_counts_mt: ps!.pct_counts_mt?.[i] ?? 0,
  }));
};

const normalizeArtifactId = (value: any): string | undefined => {
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (value && typeof value === "object") {
    const nested = value.artifactId ?? value.qcArtifactId ?? value.artifact_id ?? value.id;
    return nested == null ? undefined : String(nested);
  }
  return undefined;
};


const Results = ({ onBack, onContinue, stepResults, currentStep, onResampleQc }: ResultsProps) => {

  const { analysisData, backendResult, loading, analysisError, stepResults: savedStepResults } =
    useProjectStore();



  const [selectedTab, setSelectedTab] = useState(0);
  const projectName = analysisData?.projectName;
  const dataType = analysisData?.dataType;
  const sourceResults = useMemo(
    () => selectViewedStepResult(backendResult, stepResults, currentStep),
    [backendResult, stepResults, currentStep],
  );
  const [refreshedPreview, setRefreshedPreview] = useState<any>(null);
  const [previewRefreshError, setPreviewRefreshError] = useState("");
  const currentResults = refreshedPreview?.source === sourceResults && refreshedPreview?.project === projectName
    ? refreshedPreview.result : sourceResults;
  useEffect(() => {
    setPreviewRefreshError("");
    const request = legacyPreviewRequest(sourceResults);
    if (!projectName || !request) return;
    let cancelled = false;
    projectsService.qcResample({ accessToken: "" }, projectName, request)
      .then((preview) => {
        if (!cancelled) setRefreshedPreview({ source: sourceResults, project: projectName,
          result: mergeRefreshedPreview(sourceResults, preview) });
      })
      .catch(() => {
        if (!cancelled) setPreviewRefreshError("This saved preview could not be refreshed. The displayed point count is from the original saved result.");
      });
    return () => { cancelled = true; };
  }, [sourceResults, projectName]);
  const filteringResult = Object.values(savedStepResults || {}).find(
    (result: any) => result?.currentStep === "Filter Cells & Genes",
  ) as any;
  const analysisSampling = currentResults?.analysisSampling || (
    currentResults?.currentStep !== "Quality Control" ? filteringResult?.analysisSampling : undefined
  );


  const [tablePage, setTablePage] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(25);
  const [serverRows, setServerRows] = useState<any[]>([]);
  const [serverTotal, setServerTotal] = useState(0);
  const [sortColumn, setSortColumn] = useState<string | null>(null); // Column to sort by
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc'); // Sorting direction


  // inside Results component:
  const [clusterRows, setClusterRows] = useState<any[]>([]);
  const [selectedEnrichmentCluster, setSelectedEnrichmentCluster] = useState<string | null>(null);

 useEffect(() => {
  setSortColumn(null);
  setSortOrder("asc");
  setTablePage(0);
}, [currentResults?.currentStep]);




// Sorting rows


  React.useEffect(() => {
    const step = String(currentResults?.currentStep || "");
    const artifactSourceStep = String(currentResults?.artifactStep || step);
    const normalizedStep = step.toLowerCase().replace(/[ _-]+/g, " ").replace(/\s+/g, " ").trim();
    const supportedSteps = new Set(["quality control", "normalization", "feature selection", "umap", "clustering", "cell type annotation", "trajectory", "trajectory analysis"]);
    const artifactId = normalizeArtifactId(
      currentResults?.artifactId ||
      currentResults?.qcArtifactId ||
      currentResults?.qcMeta?.qcArtifactId
    );

    setServerRows([]);
    setServerTotal(0);
    if (!projectName || !step || !artifactId || !supportedSteps.has(normalizedStep)) return;

    let cancelled = false;
    projectsService
      .fetchRows(
        projectName,
        artifactSourceStep,
        artifactId,
        tablePage,
        tablePageSize,
        sortColumn || undefined,
        sortOrder
      )
      .then((json: any) => {
        if (cancelled) return;
        setServerRows(json.rows ?? []);
        setServerTotal(json.total ?? 0);
      })
      .catch((e: any) => {
        if (!cancelled) console.error("fetchRows failed:", e);
      });
    return () => { cancelled = true; };
  }, [
    projectName,
    currentResults?.currentStep,
    currentResults?.artifactId,
    currentResults?.qcArtifactId,
    currentResults?.qcMeta?.qcArtifactId,
    currentResults?.artifactStep,
    tablePage,
    tablePageSize,
    sortColumn,
    sortOrder,
  ]);


    // Handle sorting change from ResultsTable
    const handleSortChange = (column: string, order: 'asc' | 'desc') => {

      setSortColumn(column);
      setSortOrder(order);
    };



  // Only show success message for fresh completions, not when viewing existing results
  // If stepResults is provided, it means we're viewing existing results (no alert)
  // If only backendResult is used, it means it's a fresh completion (show alert)
  const shouldShowSuccessMessage = !stepResults && currentResults?.status === "success";
  const [showSuccessMessage, setShowSuccessMessage] = useState(shouldShowSuccessMessage);


  // Auto-close success message after 5 seconds
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (showSuccessMessage && currentResults?.status === "success") {
      const timer = setTimeout(() => {
        setShowSuccessMessage(false);
      }, 5000); // 5 seconds

      return () => clearTimeout(timer);
    }
  }, [showSuccessMessage, currentResults?.status]);
  const plotData = currentResults?.plotlyData;
  const tableData = currentResults?.tableData;

  const columns = useMemo(() => {
    if (!Array.isArray(tableData) || tableData.length === 0) return [];
    return Object.keys(tableData[0])
      .filter((key) =>
        !((key === "umap1" && tableData[0].UMAP1 != null) ||
          (key === "umap2" && tableData[0].UMAP2 != null)),
      )
      .map((key) => ({
      name: key,
      selector: (row: any) => row[key],
      sortable: true,
      wrap: true,
    }));
  }, [tableData]);

const qcRows = useMemo(() => {
  // Handle row-based, column-based, {columns,data}, and plotSample payloads.
  return getStepRows(currentResults, tableData);
}, [currentResults, tableData]);

const previewTotal = Number(currentResults?.totalCells ?? currentResults?.totalGenes ?? 0);
const previewSample = Number(currentResults?.sampleN ?? 0);
const previewUnit = currentResults?.totalCells != null ? "cells" : "genes";
const hasPreviewSummary = Number.isFinite(previewTotal) && Number.isFinite(previewSample) && previewTotal > 0 && previewSample > 0;

const hasPlotData =
  (qcRows.length > 0) ||
  (!!currentResults?.plotSample && typeof currentResults.plotSample === "object") ||
  (Array.isArray(tableData) && tableData.length > 0) ||
  (!!plotData && typeof plotData === "object") ||
  ((tableData?.plotSample ?? currentResults?.plotSample) &&
    ((tableData?.plotSample ?? currentResults?.plotSample).total_counts?.length ?? 0) > 0) ||
  (tableData &&
    typeof tableData === "object" &&
    Object.keys(tableData).length > 0);


  const memoizedPlot = useMemo(() => {
  if (!hasPlotData) return <p>No plot data available.</p>;

  // If QC v2, pass qcRows (array) so old plot components work
  const dataForPlots = (tableData?.plotSample ?? currentResults?.plotSample) ? qcRows : tableData;
  // console.log("Plot data rows:", (tableData?.plotSample ? qcRows.length : (tableData?.length ?? 0)));

  return (
    <ResultsPlots
      tableData={dataForPlots}
      plotData={plotData}
      dataType={dataType || ""}
      projectName={projectName}
      backendResult={currentResults}
      onResampleQc={(payload) => {
      onResampleQc?.(payload); // forward the object

    }}
    onClusterRowsChange={setClusterRows}
    enrichmentSelectedCluster={selectedEnrichmentCluster}
    onEnrichmentSelectedClusterChange={setSelectedEnrichmentCluster}

    />
  );
}, [hasPlotData, qcRows, tableData, plotData, dataType, currentResults,
  onResampleQc, selectedEnrichmentCluster, projectName
]);


  // const memoizedPlot = useMemo(() => {
  //   if (!tableData || tableData.length === 0)
  //     return <p>No plot data available.</p>;
  //   return (
  //     <ResultsPlots
  //       tableData={tableData}
  //       plotData={plotData}
  //       dataType={dataType || ""}
  //       backendResult={currentResults}
  //     />
  //   );
  // }, [tableData, plotData, dataType, currentResults]);


  const memoizedTable = useMemo(() => {
  const plotSample = currentResults?.plotSample as PlotSample | undefined;

  // ✅ Prefer resampled points when present
  const sampledRows = buildRowsFromPlotSample(plotSample);

  // ✅ Fallback to initial QC table rows
  const rows = sampledRows.length
    ? sampledRows
    : getStepRows(currentResults, Array.isArray(currentResults?.tableData) ? currentResults.tableData : []);

  const qcArtifactId = normalizeArtifactId(
    currentResults?.qcArtifactId ??
    currentResults?.artifactId ??
    currentResults?.qcMeta?.qcArtifactId,
  );
  const normalizedCurrentStep = String(currentResults?.currentStep ?? "").toLowerCase().replace(/[ _-]+/g, " ").replace(/\s+/g, " ").trim();
  const artifactBackedSteps = new Set(["quality control", "normalization", "feature selection", "umap", "clustering", "cell type annotation", "trajectory", "trajectory analysis"]);
  const usesServerRows = Boolean(qcArtifactId) && artifactBackedSteps.has(normalizedCurrentStep);
  const artifactSourceStep = String(currentResults?.artifactStep || currentResults?.currentStep || "");

  const isEnrichment = normalizedCurrentStep === "enrichment";
  const displayRows = isEnrichment
    ? clusterRows
    : usesServerRows && serverRows.length
      ? serverRows
      : rows;

  if (displayRows.length === 0) return <p>No table data available.</p>;

  const displayColumns = isEnrichment && clusterRows.length
    ? Object.keys(clusterRows[0]).map((k) => ({ name: k }))
    : columns.length
      ? columns
      : Object.keys(displayRows[0] ?? {})
        .filter((key) =>
          !((key === "umap1" && displayRows[0]?.UMAP1 != null) ||
            (key === "umap2" && displayRows[0]?.UMAP2 != null)),
        )
        .map((k) => ({ name: k }));
  return (
    <ResultsTable
      data={displayRows}

      columns={displayColumns}
      serverPagination={usesServerRows}
      sortColumn={sortColumn}
      sortOrder={sortOrder}
      onSortChange={handleSortChange}
      totalCount={usesServerRows ? (serverTotal || displayRows.length) : displayRows.length}
      onPageRequest={(p, ps) => {
        setTablePage(p);
        setTablePageSize(ps);
      }}
      onDownloadFullCSV={
        usesServerRows && qcArtifactId
          ? () => downloadFullArtifactCsv(projectName, artifactSourceStep, qcArtifactId)
          : undefined
      }
      fullDownloadLabel="Download CSV (all data)"
    />
  );
}, [

  currentResults,
  serverTotal,
  serverRows,
  columns,
  projectName,
  sortColumn,                        // ✅ add
  sortOrder,
  clusterRows,         // ✅ we *do* use clusterRows


]);


  if (!analysisData) {
    return <div>No analysis context available.</div>;
  }

  return (
    <Box
      sx={{
        display: "flex",
        flex: 1,
        flexDirection: "column",
        width: "100%",
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
      }}
    >
      {/* Header with View Toggle */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
        sx={{ flexShrink: 0 }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600, color: "#1f2937" }}>
            Results for <span style={{ color: "#19C1DB" }}>{projectName}</span>
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Data Type: {dataType}
          </Typography>
        </Box>

        {/* View Toggle on the right */}
        <ToggleButtonGroup
          value={selectedTab === 0 ? "plot" : "table"}
          exclusive
          onChange={(event, newValue) => {
            if (newValue !== null) {
              setSelectedTab(newValue === "plot" ? 0 : 1);
            }
          }}
          size="small"
        >
          <ToggleButton value="plot">
            <BarChartIcon sx={{ mr: 1 }} />
            Plot
          </ToggleButton>
          <ToggleButton value="table">
            <TableChartIcon sx={{ mr: 1 }} />
            Table
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {previewRefreshError && <Alert severity="warning" sx={{ mb: 2 }}>{previewRefreshError}</Alert>}
      {hasPreviewSummary && (
        <Box sx={{ mb: 2, color: "#475569", flexShrink: 0, display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography variant="body2">
            This step contains {previewTotal.toLocaleString()} {previewUnit}; the plot displays {Math.min(previewSample, previewTotal).toLocaleString()}.
          </Typography>
          <Tooltip
            arrow
            title={`The visualization is sampled for browser performance. Table pages and Download CSV (all data) use the complete ${previewTotal.toLocaleString()}-${previewUnit} dataset.`}
          >
            <IconButton size="small" aria-label="About sampled visualization and full CSV download" sx={{ p: 0.25 }}>
              <InfoOutlinedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {loading && (
        <Box display="flex" flexDirection="column" alignItems="center" py={4} sx={{ flexShrink: 0 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              border: "4px solid #f3f3f3",
              borderTop: "4px solid #19C1DB",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              "@keyframes spin": {
                "0%": { transform: "rotate(0deg)" },
                "100%": { transform: "rotate(360deg)" },
              },
            }}
          />
          <Typography variant="body1" sx={{ mt: 2, color: "#6b7280" }}>
            Loading backend results...
          </Typography>
        </Box>
      )}

      {!loading && currentResults?.status === "success" && showSuccessMessage && (
        <Alert
          severity="success"
          sx={{
            mb: 3,
            flexShrink: 0,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }
          }}
          onClose={() => setShowSuccessMessage(false)}
        >
          <span>
            Step complete:{" "}
            {currentResults.message || "Results loaded successfully!"}
          </span>
          <span style={{ fontSize: '0.75rem', opacity: 0.7, marginLeft: '8px' }}>
            (Auto-closes in 5s)
          </span>
        </Alert>
      )}

      {!loading && analysisSampling && (
        <Alert severity={analysisSampling.applied ? "warning" : "info"} sx={{ mb: 3, flexShrink: 0 }}>
          {analysisSampling.applied
            ? `${Number(analysisSampling.filteredCells).toLocaleString()} cells passed filtering. This run uses a reproducible random sample of ${Number(analysisSampling.analysedCells).toLocaleString()} cells for all downstream analyses (limit: ${Number(analysisSampling.maxCells).toLocaleString()}). Rare cell types may be missed.`
            : `All ${Number(analysisSampling.analysedCells).toLocaleString()} filtered cells are included. This server analyses up to ${Number(analysisSampling.maxCells).toLocaleString()} cells per run.`}
        </Alert>
      )}

      {analysisError && (
        <Alert severity="error" sx={{ mb: 3, flexShrink: 0 }}>
          {analysisError}
        </Alert>
      )}

      {/* Content without card styling */}
      {!loading && currentResults && (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            width: "100%",
            display: "flex",
            flexDirection: "column",
            overflowX: "hidden",
            overflowY: "scroll",
            scrollbarGutter: "stable both-edges",
            "&::-webkit-scrollbar": { width: 10 },
            "&::-webkit-scrollbar-thumb": { backgroundColor: "#cbd5e1", borderRadius: 8 },
            "&::-webkit-scrollbar-track": { backgroundColor: "#f8fafc" },
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
          }}
        >
          {selectedTab === 0 ? memoizedPlot : memoizedTable}
        </Box>
      )}
    </Box>
  );
};

export default Results;
